import type { AttentionFilter, Bin, Site, SiteFilters, SiteSort } from './types';
import { TYPE_LABEL } from './types';

/**
 * The Locations list's filtering and sorting, kept out of the page so it can be tested and so
 * the table and the map read exactly the same filtered list.
 */

export function locationCount(site: Site): number {
  return site.locations.length;
}

export function binCount(site: Site): number {
  return site.locations.reduce((total, location) => total + location.bins.length, 0);
}

export function isEmptySite(site: Site): boolean {
  return binCount(site) === 0;
}

/** Rockfall routes hold no stock; they belong on another page, not this one. */
export function isStockSite(site: Site): boolean {
  return site.type !== 'rockfall';
}

export function allBins(site: Site): Bin[] {
  return site.locations.flatMap((location) => location.bins);
}

/** Distinct materials across every bin, in alphabetical order. */
export function materialsStocked(site: Site): string[] {
  const names = new Set<string>();
  for (const bin of allBins(site)) if (bin.material) names.add(bin.material);
  return [...names].sort((a, b) => a.localeCompare(b));
}

/** Bins needing someone to act: below zero, below the minimum or above the maximum. */
export function attentionTotal(site: Site): number {
  return site.attention.negative + site.attention.belowMin + site.attention.overMax;
}

export interface MaterialOption {
  id: string;
  label: string;
}

/** Every material held in any bin across the sites, for the material filter. */
export function materialOptions(sites: Site[]): MaterialOption[] {
  const seen = new Map<string, string>();
  for (const site of sites) {
    for (const bin of allBins(site)) {
      if (bin.materialId && !seen.has(bin.materialId)) seen.set(bin.materialId, bin.material);
    }
  }
  return [...seen].map(([id, label]) => ({ id, label })).sort((a, b) => a.label.localeCompare(b.label, undefined, { numeric: true }));
}

function haystack(site: Site): string {
  return [site.name, site.number ?? '', site.description ?? '', TYPE_LABEL[site.type], site.serviceAreaName]
    .join(' ')
    .toLowerCase();
}

function matchesAttention(site: Site, attention: AttentionFilter): boolean {
  switch (attention) {
    case 'any':
      return true;
    case 'needs-action':
      return attentionTotal(site) > 0;
    case 'negative':
      return site.attention.negative > 0;
    case 'below-min':
      return site.attention.belowMin > 0;
    case 'over-max':
      return site.attention.overMax > 0;
  }
}

export function matchesFilters(site: Site, filters: SiteFilters): boolean {
  if (!isStockSite(site)) return false;
  if (filters.serviceAreaIds.length && !filters.serviceAreaIds.includes(site.serviceAreaId)) return false;
  if (filters.foremanAreaIds.length && !site.foremanAreaIds.some((id) => filters.foremanAreaIds.includes(id))) return false;
  if (filters.types.length && !(filters.types as string[]).includes(site.type)) return false;
  if (filters.owners.length && !filters.owners.includes(site.owner)) return false;
  if (filters.materialIds.length && !allBins(site).some((bin) => bin.materialId && filters.materialIds.includes(bin.materialId))) {
    return false;
  }
  if (!matchesAttention(site, filters.attention)) return false;
  const text = filters.search.trim().toLowerCase();
  if (text && !haystack(site).includes(text)) return false;
  return true;
}

/** How a column sorts. Names compare naturally ("#9" before "#10"); missing values sink. */
function sortValue(site: Site, key: SiteSort['key'], foremanAreaName: (id: string) => string): string | number {
  switch (key) {
    case 'name':
      return site.name;
    case 'type':
      return TYPE_LABEL[site.type];
    case 'owner':
      return site.owner;
    case 'serviceArea':
      return site.serviceAreaName;
    case 'foremanArea':
      return site.foremanAreaIds.map(foremanAreaName).sort().join(', ');
    case 'locations':
      return locationCount(site);
    case 'bins':
      return binCount(site);
    case 'materials':
      return materialsStocked(site).length;
    case 'attention':
      // Below zero weighs most: it is the one that means the record is wrong.
      return site.attention.negative * 1000 + site.attention.belowMin * 10 + site.attention.overMax;
    case 'lastCount':
      return site.lastCountAt ?? '';
  }
}

export function sortSites(sites: Site[], sort: SiteSort, foremanAreaName: (id: string) => string = (id) => id): Site[] {
  const factor = sort.dir === 'asc' ? 1 : -1;
  return [...sites].sort((a, b) => {
    const va = sortValue(a, sort.key, foremanAreaName);
    const vb = sortValue(b, sort.key, foremanAreaName);
    const primary =
      typeof va === 'number' && typeof vb === 'number'
        ? va - vb
        : String(va).localeCompare(String(vb), undefined, { numeric: true, sensitivity: 'base' });
    if (primary !== 0) return primary * factor;
    // Ties fall back to the name, always A to Z, so the order never jitters.
    return a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }) || a.id.localeCompare(b.id);
  });
}

export function filterSites(
  sites: Site[],
  filters: SiteFilters,
  sort: SiteSort,
  foremanAreaName?: (id: string) => string
): Site[] {
  return sortSites(sites.filter((site) => matchesFilters(site, filters)), sort, foremanAreaName);
}

/** Applied filters, counted the way the filter card shows them. Search is not a filter. */
export function activeFilterCount(filters: SiteFilters): number {
  return (
    filters.serviceAreaIds.length +
    filters.foremanAreaIds.length +
    filters.materialIds.length +
    filters.types.length +
    filters.owners.length +
    (filters.attention === 'any' ? 0 : 1)
  );
}

/** The bins a site's expanded row shows: all of them, or only the ones needing action. */
function binMatchesAttention(bin: Bin, attention: AttentionFilter): boolean {
  if (attention === 'any') return true;
  if (attention === 'needs-action') return bin.status === 'negative' || bin.status === 'below-min' || bin.status === 'over-max';
  return bin.status === attention;
}

/** The filters that narrow a site's expanded bins, not just which sites are listed. */
export type BinFilters = Pick<SiteFilters, 'materialIds' | 'attention'>;

/** True when the bin filters would hide some bins, so the panel offers "Show all". */
export const narrowsBins = (filters: BinFilters): boolean => filters.materialIds.length > 0 || filters.attention !== 'any';

/** A site's bins that match the chosen materials and needs-action state; both apply together. */
export function binsToShow(bins: Bin[], filters: BinFilters): Bin[] {
  return bins.filter(
    (bin) =>
      (!filters.materialIds.length || (bin.materialId !== null && filters.materialIds.includes(bin.materialId))) &&
      binMatchesAttention(bin, filters.attention)
  );
}
